import { useCallback, useEffect, useRef, useState } from 'react'

import { throttle } from 'es-toolkit'

/**
 * Hook to handle hover and tap events to show and hide UI elements
 */
export const useVisibilityToggle = () => {
  const [isVisible, setIsVisible] = useState(true)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const targetRef = useRef<HTMLDivElement>(null)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleInteraction = useCallback(
    // eslint-disable-next-line react-hooks/use-memo -- throttle() wrapper must stay; inlining breaks throttling
    throttle(() => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      setIsVisible(true)

      timeoutRef.current = setTimeout(() => {
        setIsVisible(false)
      }, 3000)
    }, 250),
    [],
  )

  /**
   * ADA Compliance: https://fwn.atlassian.net/browse/OTO-2043
   *
   * 1. When an element inside `targetRef` is focused:
   *    - Both `keydown` and `keyup` events are observed.
   *    - UI visibility is maintained.
   *
   * 2. When the last element in `targetRef` loses focus:
   *    - Only the `keydown` event is observed.
   *    - The auto-hide timeout is reset.
   */
  const handleKeyup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    setIsVisible(true)
  }, [])

  const handeKeydown = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      setIsVisible(false)
    }, 3000)
  }, [])

  // Auto Hide after 3 seconds when mounting
  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false)
    }, 3000)
  }, [])

  useEffect(() => {
    const target = targetRef.current
    if (target) {
      target.addEventListener('mousemove', handleInteraction)
      target.addEventListener('touchstart', handleInteraction)
      target.addEventListener('keydown', handeKeydown)
      target.addEventListener('keyup', handleKeyup)

      return () => {
        target.removeEventListener('mousemove', handleInteraction)
        target.removeEventListener('touchstart', handleInteraction)
        target.removeEventListener('keydown', handeKeydown)
        target.removeEventListener('keyup', handleKeyup)
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
      }
    }
  }, [handleInteraction, handeKeydown, handleKeyup])

  return { isVisible, ref: targetRef }
}
